/**
 * Centralized chart theme tokens and builder functions.
 *
 * ALL chart styling lives here — AG-Charts themes and series defaults.
 * Widget renderers in items/ should never contain hardcoded hex colors,
 * font sizes, or styling constants. Instead they import from this module
 * (via design/index.ts) and pass tokens to chart blocks.
 *
 * Change a value here → every widget updates automatically.
 */

import { CHART_SERIES_COLORS, CHART_SERIES_COLORS_DARK } from './theme';
import type { ThemeMode } from '../../../types';

// ---------------------------------------------------------------------------
// Canonical default color palette (superset of CHART_SERIES_COLORS)
// ---------------------------------------------------------------------------

/** 8-color default palette used by chart blocks and builders */
export const DEFAULT_COLORS: string[] = [
  ...CHART_SERIES_COLORS,
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
];

// ---------------------------------------------------------------------------
// AG-Charts theme tokens
// ---------------------------------------------------------------------------

const FONT_FAMILY = 'Inter, system-ui, sans-serif';

export const AG_CHART_TOKENS = {
  font: {
    family: FONT_FAMILY,
  },
  title: {
    fontSize: 14,
    fontWeight: 600 as const,
    color: '#374151',       // neutral-700
    fontFamily: FONT_FAMILY,
  },
  axis: {
    label: {
      color: '#6b7280',     // neutral-500
      fontSize: 11,
      fontFamily: FONT_FAMILY,
      autoRotate: false,
      autoRotateAngle: 335,
    },
    line: { color: '#e5e7eb' },   // neutral-200
    tick: { color: '#e5e7eb' },
    gridLine: {
      style: [{ stroke: '#f3f4f6', lineDash: [4, 4] }] as Array<{ stroke: string; lineDash: number[] }>,
    },
  },
} as const;

// ---------------------------------------------------------------------------
// Per-chart-type series defaults
// ---------------------------------------------------------------------------

export const SERIES_DEFAULTS = {
  bar: {
    cornerRadius: 4,
  },
  line: {
    strokeWidth: 2,
    marker: {
      size: 6,
      strokeWidth: 2,
      stroke: '#ffffff',
    },
  },
  area: {
    fillOpacity: 0.15,
    strokeWidth: 2,
    marker: {
      size: 5,
    },
  },
  pie: {
    strokeWidth: 2,
    pieStrokeColor: '#ffffff',
  },
} as const;

// ---------------------------------------------------------------------------
// Builder functions
// ---------------------------------------------------------------------------

// Theme-specific tokens
const LIGHT_TOKENS = {
  title: { color: '#374151' },       // neutral-700
  label: { color: '#6b7280' },       // neutral-500
  line: { color: '#e5e7eb' },        // neutral-200
  tick: { color: '#e5e7eb' },
  gridLine: { stroke: '#f3f4f6' },   // neutral-100
};

const DARK_TOKENS = {
  title: { color: '#e5e7eb' },       // neutral-200
  label: { color: '#9ca3af' },       // neutral-400
  line: { color: '#374151' },        // neutral-700
  tick: { color: '#374151' },
  gridLine: { stroke: '#1f2937' },   // neutral-800
};

export function getThemeTokens(mode: ThemeMode) {
  return mode === 'dark' ? DARK_TOKENS : LIGHT_TOKENS;
}

/** Build a complete AG-Charts theme object from a color palette and theme mode */
export function buildAgChartTheme(
  colors: string[],
  mode: ThemeMode = 'light',
  opts?: {
    /** Does the chart have a Y axis on the left side? */
    hasLeftAxis?: boolean;
    /** Does the chart have a Y axis on the right side? */
    hasRightAxis?: boolean;
    /** Does the chart show end-value tags? */
    hasEndValueTags?: boolean;
  },
) {
  const t = getThemeTokens(mode);
  // Dynamic padding based on axis layout
  const leftPad = opts?.hasLeftAxis ? 4 : 20;   // No left axis → pad for first X label
  const rightPad = opts?.hasRightAxis ? (opts?.hasEndValueTags ? 12 : 4) : 20; // No right axis → pad for last X label

  return {
    baseTheme: mode === 'dark' ? 'ag-default-dark' as const : 'ag-default' as const,
    palette: {
      fills: [...colors],
      strokes: [...colors],
    },
    overrides: {
      common: {
        title: {
          fontSize: AG_CHART_TOKENS.title.fontSize,
          fontWeight: AG_CHART_TOKENS.title.fontWeight,
          fontFamily: AG_CHART_TOKENS.title.fontFamily,
          color: t.title.color,
        },
        padding: { top: 12, right: rightPad + 4, bottom: 6, left: leftPad + 4 },
        legend: {
          spacing: 4,
          item: { paddingX: 8, paddingY: 4 },
        },
      },
      cartesian: {
        axes: {
          category: {
            label: {
              ...AG_CHART_TOKENS.axis.label,
              color: t.label.color,
            },
            line: { ...t.line },
            tick: { ...t.tick },
          },
          number: {
            label: {
              fontSize: AG_CHART_TOKENS.axis.label.fontSize,
              fontFamily: AG_CHART_TOKENS.axis.label.fontFamily,
              color: t.label.color,
              padding: 2,
            },
            line: { ...t.line },
            tick: { ...t.tick, size: 4 },
            gridLine: { style: [{ stroke: t.gridLine.stroke, lineDash: [4, 4] }] },
            thickness: 40,
          },
        },
      },
    },
  };
}

/** Build standard cartesian axes (dictionary format for AG-Charts) */
export function buildCartesianAxes(overrides?: {
  xLabelFormatter?: (params: any) => string;
  yLabelFormatter?: (params: any) => string;
  yPosition?: 'left' | 'right';
  xGridLine?: object;
  yGridLine?: object;
  /** Lock Y axis range so it doesn't rescale when series are hidden */
  fixedRange?: { min: number; max: number };
}) {
  return {
    x: {
      type: 'category' as const,
      position: 'bottom' as const,
      label: {
        autoRotate: false,
        autoRotateAngle: AG_CHART_TOKENS.axis.label.autoRotateAngle,
        ...(overrides?.xLabelFormatter ? { formatter: overrides.xLabelFormatter } : {}),
      },
      ...(overrides?.xGridLine ? { gridLine: overrides.xGridLine } : {}),
    },
    y: {
      type: 'number' as const,
      position: (overrides?.yPosition ?? 'right') as 'left' | 'right',
      gridLine: overrides?.yGridLine ?? { style: [...AG_CHART_TOKENS.axis.gridLine.style] },
      ...(overrides?.yLabelFormatter ? { label: { formatter: overrides.yLabelFormatter } } : {}),
      ...(overrides?.fixedRange ? { min: overrides.fixedRange.min, max: overrides.fixedRange.max } : {}),
      thickness: 40,
    },
  };
}

// ---------------------------------------------------------------------------
// Nice range computation (matches AG-Charts axis scaling)
// ---------------------------------------------------------------------------

function niceStep(rawStep: number): number {
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  if (norm <= 1) return mag;
  if (norm <= 2) return 2 * mag;
  if (norm <= 5) return 5 * mag;
  return 10 * mag;
}

/** Compute a nice axis range — rounds min/max to clean tick boundaries */
export function computeNiceRange(dataMin: number, dataMax: number): { min: number; max: number } {
  if (dataMin === dataMax) return { min: dataMin - 1, max: dataMax + 1 };
  const range = dataMax - dataMin;
  const paddedMin = dataMin - range * 0.05;
  const paddedMax = dataMax + range * 0.05;
  const step = niceStep((paddedMax - paddedMin) / 5);
  return {
    min: Math.floor(paddedMin / step) * step,
    max: Math.ceil(paddedMax / step) * step,
  };
}

/** Compute a fixed Y range from all values of the given keys across all data rows */
export function computeFixedRange(
  data: Record<string, unknown>[],
  yKeys: string[],
  stacked?: boolean,
): { min: number; max: number } {
  const values: number[] = [];
  if (stacked) {
    for (const row of data) {
      let cum = 0;
      for (const key of yKeys) {
        const v = row[key];
        if (typeof v === 'number') cum += v;
      }
      values.push(cum);
      values.push(0);
    }
  } else {
    for (const key of yKeys) {
      for (const row of data) {
        const v = row[key];
        if (typeof v === 'number') values.push(v);
      }
    }
  }
  if (values.length === 0) return { min: 0, max: 1 };
  return computeNiceRange(Math.min(...values), Math.max(...values));
}

// ---------------------------------------------------------------------------
// End-value axis tags (crossLines with invisible line + colored label)
// ---------------------------------------------------------------------------

interface EndValueSeriesDef {
  yKey: string;
  yName: string;
  color?: string;
}

/** Build crossLine configs to show end-value tags on the Y axis.
 *  When stacked=true, crossLine values are cumulative (matching visual stack positions).
 *  Labels that would overlap are nudged apart. */
export function buildEndValueTags(
  data: Record<string, unknown>[],
  series: EndValueSeriesDef[],
  colors: string[],
  formatter?: (value: number) => string,
  labelPosition: 'left' | 'right' = 'right',
  stacked?: boolean,
): Record<string, unknown>[] {
  if (data.length === 0) return [];
  const lastRow = data[data.length - 1];

  // First pass: compute raw crossLine values
  const entries: Array<{ color: string; text: string; value: number }> = [];
  let cumulative = 0;

  for (let i = 0; i < series.length; i++) {
    const s = series[i];
    const rawValue = lastRow[s.yKey];
    if (rawValue == null || typeof rawValue !== 'number') continue;
    const color = s.color || colors[i % colors.length];
    const text = formatter ? formatter(rawValue) : rawValue.toLocaleString();

    let crossLineValue: number;
    if (stacked) {
      cumulative += rawValue;
      crossLineValue = cumulative;
    } else {
      crossLineValue = rawValue;
    }

    entries.push({ color, text, value: crossLineValue });
  }

  if (entries.length === 0) return [];

  // Second pass: nudge overlapping labels apart
  // Sort by value, then push apart any that are too close
  entries.sort((a, b) => a.value - b.value);
  const allValues = entries.map((e) => e.value);
  const range = Math.max(...allValues) - Math.min(...allValues) || 1;
  const minGap = range * 0.08; // ~8% of data range as minimum gap between labels

  // Multiple passes to resolve cascading overlaps
  for (let pass = 0; pass < 5; pass++) {
    let changed = false;
    for (let i = 1; i < entries.length; i++) {
      const gap = entries[i].value - entries[i - 1].value;
      if (gap < minGap) {
        const deficit = minGap - gap;
        entries[i - 1].value -= deficit / 2;
        entries[i].value += deficit / 2;
        changed = true;
      }
    }
    if (!changed) break;
  }

  return entries.map((e) => ({
    type: 'line',
    value: e.value,
    strokeWidth: 0,
    strokeOpacity: 0,
    label: {
      text: e.text,
      fontSize: 9,
      fontWeight: 700,
      fontFamily: FONT_FAMILY,
      color: e.color,
      padding: 2,
      position: labelPosition,
    },
  }));
}

// ---------------------------------------------------------------------------
// Legend config with optional last values
// ---------------------------------------------------------------------------

/** Build legend config, optionally appending last values to series names */
export function buildLegendConfig(opts: {
  enabled: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
  toggleSeries?: boolean;
  legendValues?: boolean;
  data?: Record<string, unknown>[];
  series?: Array<{ yKey: string; yName: string }>;
  valueFormatter?: (value: number) => string;
}) {
  const legend: Record<string, unknown> = {
    enabled: opts.enabled,
    position: opts.position ?? 'bottom',
    toggleSeries: opts.toggleSeries ?? true,
    spacing: 4,
    item: { paddingX: 8, paddingY: 4 },
  };

  if (opts.legendValues && opts.data?.length && opts.series?.length) {
    const lastRow = opts.data[opts.data.length - 1];
    const fmt = opts.valueFormatter || ((v: number) => v.toLocaleString());

    // AG-Charts legend label formatter receives { itemId, seriesId, value (text) }
    legend.item = {
      ...legend.item as object,
      label: {
        formatter: (params: { value: string }) => {
          // Find the series matching this legend label
          const match = opts.series!.find((s) => s.yName === params.value);
          if (!match) return params.value;
          const val = lastRow[match.yKey];
          if (val == null || typeof val !== 'number') return params.value;
          return `${params.value}  ${fmt(val)}`;
        },
      },
    };
  }

  return legend;
}

// ---------------------------------------------------------------------------
// Axis layout — auto-assign positions from axis IDs
// ---------------------------------------------------------------------------

export interface AxisLayout {
  /** Axis ID → dictionary key used in AG-Charts axes config */
  key: string;
  /** Which side this axis renders on */
  position: 'left' | 'right';
  /** Whether this axis shows grid lines (only the first axis does) */
  showGrid: boolean;
}

/**
 * Compute axis positions from a list of unique axis IDs.
 * Rules:
 *   1 axis  → right
 *   2 axes  → 1st left, 2nd right
 *   3+ axes → 1st left, rest on right
 */
export function computeAxisLayout(axisIds: string[]): Map<string, AxisLayout> {
  const unique = Array.from(new Set(axisIds));
  const map = new Map<string, AxisLayout>();

  if (unique.length <= 1) {
    const id = unique[0] || 'a';
    map.set(id, { key: 'y', position: 'right', showGrid: true });
  } else {
    unique.forEach((id, i) => {
      if (i === 0) {
        map.set(id, { key: 'y', position: 'left', showGrid: true });
      } else {
        map.set(id, { key: `y${i}`, position: 'right', showGrid: false });
      }
    });
  }

  return map;
}

/** Build dictionary-style AG-Charts axes from axis layout + config */
export function buildMultiAxes(opts: {
  layout: Map<string, AxisLayout>;
  mode: ThemeMode;
  xLabelFormatter?: (params: any) => string;
  yLabelFormatters?: Record<string, (params: any) => string>;
  /** Lock Y axis ranges per axis ID so they don't rescale when series are hidden */
  fixedRanges?: Record<string, { min: number; max: number }>;
}) {
  const t = getThemeTokens(opts.mode);

  const axes: Record<string, unknown> = {
    x: {
      type: 'category' as const,
      position: 'bottom' as const,
      label: {
        autoRotate: false,
        autoRotateAngle: AG_CHART_TOKENS.axis.label.autoRotateAngle,
        ...(opts.xLabelFormatter ? { formatter: opts.xLabelFormatter } : {}),
      },
    },
  };

  opts.layout.forEach((layout, axisId) => {
    const formatter = opts.yLabelFormatters?.[axisId];
    const fixedRange = opts.fixedRanges?.[axisId];
    axes[layout.key] = {
      type: 'number' as const,
      position: layout.position,
      label: {
        color: t.label.color,
        fontSize: AG_CHART_TOKENS.axis.label.fontSize,
        fontFamily: AG_CHART_TOKENS.axis.label.fontFamily,
        padding: 2,
        ...(formatter ? { formatter } : {}),
      },
      line: { ...t.line },
      tick: { ...t.tick, size: 4 },
      gridLine: layout.showGrid
        ? { style: [{ stroke: t.gridLine.stroke, lineDash: [4, 4] }] }
        : { style: [{ stroke: 'transparent' }] },
      ...(fixedRange ? { min: fixedRange.min, max: fixedRange.max } : {}),
      thickness: 40,
    };
  });

  return axes;
}

// Backward compat wrapper
/** @deprecated Use buildMultiAxes with computeAxisLayout instead */
export function buildDualAxes(opts: {
  mode: ThemeMode;
  xLabelFormatter?: (params: any) => string;
  leftAxisLabel?: string;
  rightAxisLabel?: string;
  leftYLabelFormatter?: (params: any) => string;
  rightYLabelFormatter?: (params: any) => string;
  leftCrossLines?: Record<string, unknown>[];
  rightCrossLines?: Record<string, unknown>[];
}) {
  const layout = new Map<string, AxisLayout>();
  layout.set('a', { key: 'y', position: 'left', showGrid: true });
  layout.set('b', { key: 'y1', position: 'right', showGrid: false });

  const yFormatters: Record<string, (params: any) => string> = {};
  if (opts.leftYLabelFormatter) yFormatters['a'] = opts.leftYLabelFormatter;
  if (opts.rightYLabelFormatter) yFormatters['b'] = opts.rightYLabelFormatter;

  return buildMultiAxes({
    layout,
    mode: opts.mode,
    xLabelFormatter: opts.xLabelFormatter,
    yLabelFormatters: yFormatters,
  });
}

/** Get series styling defaults for a given chart type */
export function getSeriesDefaults(chartType: 'bar' | 'line' | 'area' | 'pie' | 'donut') {
  switch (chartType) {
    case 'bar':
      return { ...SERIES_DEFAULTS.bar };
    case 'line':
      return {
        strokeWidth: SERIES_DEFAULTS.line.strokeWidth,
        marker: { ...SERIES_DEFAULTS.line.marker },
      };
    case 'area':
      return {
        fillOpacity: SERIES_DEFAULTS.area.fillOpacity,
        strokeWidth: SERIES_DEFAULTS.area.strokeWidth,
        marker: { ...SERIES_DEFAULTS.area.marker },
      };
    case 'pie':
    case 'donut':
      return {
        strokeWidth: SERIES_DEFAULTS.pie.strokeWidth,
      };
    default:
      return { ...SERIES_DEFAULTS.bar };
  }
}
